import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuoteApprovalApiService } from '../../services/quote-approval-api.service';
import { QuoteApproverApiService } from '../../services/quote-approver-api.service';
import { QuoteApprovalRequest, QuoteApprovalResponse } from '../../models/quote-approval.model';
import { QuoteApproverResponse } from '../../models/quote-approver.model';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';

/**
 * Modal "Solicitar aprobación interna" (RF-001): selector de aprobador poblado desde
 * los Responsables de aprobación registrados (GET /api/quote-approvers) + comentario
 * opcional (máx 2000). El approverUserId enviado es el userId del responsable.
 * Hace el POST request-approval y notifica con toast; el padre solo refresca al recibir `requested`.
 */
@Component({
  selector: 'app-quote-request-approval-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './quote-request-approval-modal.component.html'
})
export class QuoteRequestApprovalModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() quoteId: number | null = null;
  @Input() quoteNumber = '';

  @Output() closed = new EventEmitter<void>();
  @Output() requested = new EventEmitter<QuoteApprovalResponse>();

  // Responsables de aprobación registrados (quote_approver). El valor seleccionado es su userId.
  approvers: QuoteApproverResponse[] = [];
  loadingUsers = false;
  approverId: number | null = null;
  comment = '';
  submitting = false;
  error = '';

  readonly COMMENT_MAX = 2000;

  constructor(
    private approvalApi: QuoteApprovalApiService,
    private approverApi: QuoteApproverApiService,
    private authService: AuthService,
    private toast: ToastService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.approverId = null;
      this.comment = '';
      this.error = '';
      this.submitting = false;
      this.cargarResponsables();
    }
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private cargarResponsables(): void {
    const currentUserId = this.authService.getCurrentUser()?.id;
    this.loadingUsers = true;
    this.approverApi.getApprovers(this.businessId).subscribe({
      next: (approvers) => {
        // El aprobador debe ser distinto del solicitante: excluir al usuario actual.
        this.approvers = approvers.filter(a => a.userId !== currentUserId);
        this.loadingUsers = false;
      },
      error: (err) => {
        this.loadingUsers = false;
        this.error = err.error?.message || 'No se pudo cargar la lista de responsables';
      }
    });
  }

  approverLabel(approver: QuoteApproverResponse): string {
    const name = approver.userFullName?.trim() || approver.userEmail;
    return approver.cargo ? `${name} — ${approver.cargo}` : name;
  }

  get commentLength(): number {
    return this.comment.length;
  }

  enviar(): void {
    if (this.submitting || !this.quoteId) return;
    this.error = '';

    if (this.approverId == null) {
      this.error = 'Selecciona un aprobador para enviar la solicitud';
      return;
    }
    if (this.comment.length > this.COMMENT_MAX) {
      this.error = `El comentario no puede superar los ${this.COMMENT_MAX} caracteres`;
      return;
    }

    const request: QuoteApprovalRequest = { approverUserId: this.approverId };
    const comment = this.comment.trim();
    if (comment) request.comment = comment;

    this.submitting = true;
    this.approvalApi.requestApproval(this.quoteId, this.businessId, request).subscribe({
      next: (approval) => {
        this.submitting = false;
        this.toast.success({
          title: 'Solicitud enviada',
          message: `Solicitud de aprobación enviada a ${approval.approverName}`
        });
        this.requested.emit(approval);
      },
      error: (err) => {
        this.submitting = false;
        // 409 = ya hay una solicitud PENDING; 422 = no está en Borrador / aprobador inválido.
        // El mensaje del backend es la fuente de verdad: mostrarlo inline en el modal.
        const msg = err.error?.message || 'No se pudo enviar la solicitud de aprobación';
        this.error = msg;
        // 422 (aprobador no válido / no es responsable activo): además del banner inline, toast con el mensaje del backend.
        if (err?.status === 422) {
          this.toast.error({ title: 'Aprobador no válido', message: msg });
        }
      }
    });
  }

  cancelar(): void {
    if (this.submitting) return;
    this.closed.emit();
  }
}
